import { TARGET_YAW_ANGLE } from "@/domain/yaw-check";

interface YawGaugeProps {
  /** Static (primary) yaw angle in degrees */
  staticYaw: number;
  /** Min yaw angle in degrees */
  minYaw: number;
  /** Max yaw angle in degrees */
  maxYaw: number;
}

/** Visible gauge range: 0° to 2× target (0–0.6°). Values outside are clamped. */
const GAUGE_MAX = TARGET_YAW_ANGLE * 2;

function toPercent(value: number): number {
  return Math.max(0, Math.min(100, (value / GAUGE_MAX) * 100));
}

/**
 * A compact horizontal gauge showing min/static/max yaw angles
 * relative to the 0.3° target. Green at center, amber/red at edges.
 */
export function YawGauge({ staticYaw, minYaw, maxYaw }: YawGaugeProps) {
  const staticPct = toPercent(staticYaw);
  const minPct = toPercent(minYaw);
  const maxPct = toPercent(maxYaw);
  const targetPct = toPercent(TARGET_YAW_ANGLE);
  const allZero = staticYaw === 0 && minYaw === 0 && maxYaw === 0;
  // Hide min/max labels when the range collapses — otherwise three labels
  // stack at the same X and render as illegible noise.
  const hasRange = Math.abs(maxYaw - minYaw) >= 0.01;

  return (
    <div className="relative w-full min-w-[80px] h-5">
      {/* Gauge track area — vertically centered in upper portion */}
      <div className="absolute inset-x-0 top-0 h-3">
        {/* Gradient track */}
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full"
          style={{
            // Gauge spans 0°–0.6°. Target is 0.3° (50%), acceptable range 0.15°–0.45° (25%–75%).
            // red → amber → green (25%) ── green ── (75%) green → amber → red
            background: `linear-gradient(to right,
              #ef4444 0%,
              #f59e0b 15%,
              #22c55e 25%,
              #22c55e 75%,
              #f59e0b 85%,
              #ef4444 100%)`,
          }}
        />

        {/* Target line */}
        <div
          className="absolute top-0 bottom-0 w-px bg-foreground/20"
          style={{ left: `${targetPct}%` }}
        />

        {!allZero && (
          <>
            {/* Range bar (min to max) */}
            <div
              className="absolute top-1/2 -translate-y-1/2 h-1 bg-foreground/15 rounded-full"
              style={{
                left: `${minPct}%`,
                width: `${Math.max(maxPct - minPct, 1)}%`,
              }}
            />

            {/* Min tick */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-foreground/40 rounded-full"
              style={{ left: `${minPct}%` }}
            />

            {/* Max tick */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-foreground/40 rounded-full"
              style={{ left: `${maxPct}%` }}
            />

            {/* Static dot (primary indicator) */}
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full bg-foreground ring-1 ring-background"
              style={{ left: `${staticPct}%` }}
            />
          </>
        )}
      </div>

      {/* Labels below the gauge */}
      {!allZero && (
        <div className="absolute inset-x-0 bottom-0 h-[8px]">
          {hasRange && (
            <span
              className="absolute text-[8px] leading-none text-muted-foreground -translate-x-1/2"
              style={{ left: `${minPct}%` }}
            >
              min
            </span>
          )}
          <span
            className="absolute text-[8px] leading-none text-muted-foreground -translate-x-1/2 font-medium"
            style={{ left: `${staticPct}%` }}
          >
            {staticYaw.toFixed(2)}°
          </span>
          {hasRange && (
            <span
              className="absolute text-[8px] leading-none text-muted-foreground -translate-x-1/2"
              style={{ left: `${maxPct}%` }}
            >
              max
            </span>
          )}
        </div>
      )}
    </div>
  );
}
